import { CONNECTION_INACTIVE_CODE, JournalApiError, JournalService, TEMPLATE_INACTIVE_CODE } from './JournalService';
import { JournalLine, JournalTemplate, ResolvedJournalSheet, ValidationError } from '../types/journal';
import { countBySeverity, sheetMisfitMessage, validationFoundMessage, validationPassedMessage } from '../utils/journalMessages';
import { journalFingerprint, journalNameFor, sendNeedsCheck } from '../utils/journalSheetState';

/** The backend refuses a longer journal name, as an instance's name and as a version's journalName alike. */
export const MAX_DESCRIPTION_LENGTH = 100;

/** What a send would carry, read from the sheet. */
export interface SheetReading {
  lines: JournalLine[];
  journalName: string;
  fingerprint: string;
}

/** A check that passed: the version it made, and how many warnings it listed. */
export interface PassedCheck {
  versionId: string;
  warnings: number;
}

/** What the flow needs from the task pane. */
export interface JournalFlowUi {
  status(message: string, type: 'success' | 'error' | 'info'): void;
  /** A check begins reading the sheet; a change to it from here on makes its pass stale. */
  reading(worksheetId: string): void;
  /** A passing check's message, which a change to that sheet makes stale. */
  passed(message: string, worksheetId: string): void;
  showErrors(errors: ValidationError[]): void;
  hideErrors(): void;
  setSubmitEnabled(enabled: boolean): void;
  /** What a passing check says the journal is ready for, from the actions the user has. */
  readyFor(): string;
  /** The template is no longer active in the Bifrost Portal: says so and reads the template list again. */
  templateInactive(): Promise<void>;
  /** The ERP connection is no longer active: says so and reads the tab's rights and connection again. */
  connectionInactive(): Promise<void>;
}

export type JournalFlowService = Pick<JournalService,
  'headerFit' | 'readJournalLines' | 'readJournalDescription' | 'createInstance' | 'createVersion'
  | 'updateSheetState' | 'clearErrors' | 'validate' | 'renderErrors' | 'refreshLists' | 'listOwner'>;

function reasonOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * "Check for errors", and what every send does first: a send carries exactly what the sheet reads.
 * The version the last passing check made goes as it is while the sheet still reads the same;
 * anything else is saved as a new version and checked, and goes only if that check passes.
 */
export class JournalSendFlow {
  constructor(
    private readonly service: JournalFlowService,
    private readonly ui: JournalFlowUi,
    private readonly activeConnectionId: () => Promise<string | null>,
    private readonly today: () => string = () => new Date().toISOString().slice(0, 10),
  ) {}

  /** The lines and the journal's name, or null after saying why the sheet cannot be sent. Never writes. */
  async read(sheet: ResolvedJournalSheet, template: JournalTemplate): Promise<SheetReading | null> {
    this.ui.status('Reading journal lines...', 'info');
    this.ui.reading(sheet.worksheetId);
    // Lines are read by column position, so a sheet whose columns no longer line up with the
    // template would put values in the wrong fields.
    if (await this.service.headerFit(sheet, template) === 'different') {
      this.ui.status(sheetMisfitMessage(sheet.worksheetName), 'error');
      return null;
    }
    const lines = await this.service.readJournalLines(template, sheet.worksheetId, sheet.headerRow);
    if (lines.length === 0) {
      this.ui.status(`No data rows found in "${sheet.worksheetName}"`, 'error');
      return null;
    }
    const journalName = journalNameFor(await this.service.readJournalDescription(sheet, template), sheet.manifest);
    if (journalName.length > MAX_DESCRIPTION_LENGTH) {
      this.ui.status(`The journal description can be at most ${MAX_DESCRIPTION_LENGTH} characters.`, 'error');
      return null;
    }
    return { lines, journalName, fingerprint: journalFingerprint(lines, journalName) };
  }

  /**
   * Saves what the sheet reads as a new version and checks it. The sheet's fingerprint is dropped
   * with every new version and recorded only when its check passes. Answers the version on a pass.
   */
  async check(sheet: ResolvedJournalSheet, template: JournalTemplate, reading: SheetReading): Promise<PassedCheck | null> {
    // Any outcome other than a clean pass leaves the journal unsubmittable.
    this.ui.setSubmitEnabled(false);
    // Taken now: the lists stay the company's and user's the check began as.
    const owner = this.service.listOwner();

    // Reuse the instance recorded on the sheet's manifest — it survives a
    // save/reopen, so revalidating never creates a second ERP draft.
    const instanceId = sheet.manifest.instanceId ?? await this.createInstance(sheet, template, reading);
    if (!instanceId) return null;

    this.ui.status(`Creating version with ${reading.lines.length} lines...`, 'info');
    let created: { id: string; createdById?: string };
    try {
      created = await this.service.createVersion(instanceId, reading.lines, reading.journalName);
    } catch (error) {
      this.ui.status(reasonOf(error, 'Failed to create journal version'), 'error');
      return null;
    }
    const versionId = created.id;
    sheet.manifest = await this.service.updateSheetState(sheet.manifest, {
      versionId,
      checkedFingerprint: undefined,
      preparedBy: created.createdById,
      // A newer version drops the submission; checking the version that was submitted keeps it.
      submittedBy: versionId === sheet.manifest.versionId ? sheet.manifest.submittedBy : undefined,
    });

    this.ui.status('Validating...', 'info');
    await this.service.clearErrors(template, sheet.worksheetId, sheet.headerRow);
    const result = await this.service.validate(instanceId, versionId);
    // After the check, which re-reads a list from the ERP when a value is missing from it, so values
    // added in the ERP since the sheet was made reach its dropdowns. Best effort, and not waited for.
    void this.service.refreshLists(template, sheet, owner);

    if (!result.valid) {
      this.ui.status(validationFoundMessage(result.errors), 'error');
      this.ui.showErrors(result.errors);
      await this.service.renderErrors(result.errors, template, sheet.worksheetId, sheet.headerRow);
      return null;
    }

    sheet.manifest = await this.service.updateSheetState(sheet.manifest, { checkedFingerprint: reading.fingerprint });
    const warnings = countBySeverity(result.errors).warnings;
    this.ui.passed(validationPassedMessage(reading.lines.length, this.ui.readyFor(), warnings, result.erpChecked === true), sheet.worksheetId);
    this.ui.setSubmitEnabled(true);
    if (result.errors.length > 0) {
      this.ui.showErrors(result.errors);
      await this.service.renderErrors(result.errors, template, sheet.worksheetId, sheet.headerRow);
    } else {
      this.ui.hideErrors();
    }
    return { versionId, warnings };
  }

  /**
   * The version a send carries, or null after saying why nothing can go. A check the send had to run
   * that passed with warnings stops it, so the warnings are read before anything goes; the next
   * click finds the sheet unchanged and sends.
   */
  async versionToSend(sheet: ResolvedJournalSheet, template: JournalTemplate): Promise<string | null> {
    const reading = await this.read(sheet, template);
    if (!reading) return null;
    const checked = sheet.manifest.versionId;
    if (checked && !sendNeedsCheck(sheet.manifest, reading.fingerprint)) return checked;
    const passed = await this.check(sheet, template, reading);
    if (!passed) return null;
    if (passed.warnings > 0) {
      this.ui.status(`Not sent: checking it first found ${passed.warnings} warning(s), listed below. Click again to send it as it is.`, 'info');
      return null;
    }
    return passed.versionId;
  }

  /** Runs `action` with the version to send; never when there is none. */
  async send<T>(sheet: ResolvedJournalSheet, template: JournalTemplate, action: (versionId: string) => Promise<T>): Promise<T | null> {
    const versionId = await this.versionToSend(sheet, template);
    return versionId ? action(versionId) : null;
  }

  private async createInstance(sheet: ResolvedJournalSheet, template: JournalTemplate, reading: SheetReading): Promise<string | null> {
    this.ui.status('Creating journal instance...', 'info');
    const connectionId = await this.activeConnectionId();
    if (!connectionId) {
      this.ui.status('No active ERP connection found. Configure one in the web portal.', 'error');
      return null;
    }
    let created: { id: string; erpHasDraftState?: boolean };
    try {
      created = await this.service.createInstance(template.id, connectionId, reading.journalName, this.today());
    } catch (error) {
      // The template or the connection went inactive since the pane read them. Refused before anything
      // was written, so no check beforehand is needed.
      if (error instanceof JournalApiError && error.code === TEMPLATE_INACTIVE_CODE) {
        await this.ui.templateInactive();
        return null;
      }
      if (error instanceof JournalApiError && error.code === CONNECTION_INACTIVE_CODE) {
        await this.ui.connectionInactive();
        return null;
      }
      this.ui.status(reasonOf(error, 'Failed to create journal instance'), 'error');
      return null;
    }
    // Kept with the journal, so a Push warning knows whether sending it posts without reading it back.
    const draftState = typeof created.erpHasDraftState === 'boolean' ? { erpHasDraftState: created.erpHasDraftState } : {};
    sheet.manifest = await this.service.updateSheetState(sheet.manifest, { instanceId: created.id, ...draftState });
    return created.id;
  }
}
